export type PackagingItemDocumentType =
  | 'DIELINE_DIAGRAM'
  | 'PACKAGING_PHOTOS'
  | 'LABEL_PHOTOS'
  | 'SDS_OR_BOM'
  | 'HEAVY_METALS_TEST_REPORT'
  | 'HEAVY_METALS_CONFORMITY_STATEMENT'
  | 'SUBSTANCES_OF_CONCERN_TEST_REPORT'
  | 'SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT'
  | 'OTHER'
  | 'OTHER_TEST_REPORT'

export const SINGLE_UPLOAD_PACKAGING_DOCUMENT_TYPES: PackagingItemDocumentType[] =
  ['HEAVY_METALS_CONFORMITY_STATEMENT', 'SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT']

export const PACKAGING_ITEM_DOCUMENT_XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const PACKAGING_ITEM_DOCUMENT_XLSM_MIME =
  'application/vnd.ms-excel.sheet.macroEnabled.12'

/** MIME + extension allowlist for packaging item uploads (RAG-ingestible formats). */
export const PACKAGING_ITEM_DOCUMENT_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/bmp,image/tiff,' +
  `${PACKAGING_ITEM_DOCUMENT_XLSX_MIME},${PACKAGING_ITEM_DOCUMENT_XLSM_MIME},` +
  '.pdf,.jpg,.jpeg,.jfif,.png,.bmp,.tif,.tiff,.xlsx,.xlsm'

const PACKAGING_ALLOWED_CONTENT_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/tiff',
  PACKAGING_ITEM_DOCUMENT_XLSX_MIME,
  PACKAGING_ITEM_DOCUMENT_XLSM_MIME,
])

function packagingFileNameExtension(fileName: string): string {
  const lowerCaseFileName = fileName.toLowerCase().trim()
  const lastDotIndex = lowerCaseFileName.lastIndexOf('.')
  if (lastDotIndex <= 0 || lastDotIndex === lowerCaseFileName.length - 1) {
    return ''
  }
  return lowerCaseFileName.slice(lastDotIndex)
}

function hasAllowedPackagingFileExtension(fileName: string): boolean {
  const extension = packagingFileNameExtension(fileName)
  if (!extension) {
    return false
  }
  return (
    extension === '.pdf' ||
    extension === '.jpg' ||
    extension === '.jpeg' ||
    extension === '.jfif' ||
    extension === '.png' ||
    extension === '.bmp' ||
    extension === '.tif' ||
    extension === '.tiff' ||
    extension === '.xlsx' ||
    extension === '.xlsm'
  )
}

function isIgnorableBrowserContentType(contentType: string): boolean {
  const normalized = contentType.trim().toLowerCase()
  return normalized.length === 0 || normalized === 'application/octet-stream'
}

export function isAllowedPackagingItemDocumentFile(file: File): boolean {
  if (!hasAllowedPackagingFileExtension(file.name)) {
    return false
  }
  if (isIgnorableBrowserContentType(file.type)) {
    return true
  }
  return PACKAGING_ALLOWED_CONTENT_TYPES.has(file.type.trim().toLowerCase())
}

function resolvePackagingContentTypeFromExtension(fileName: string): string {
  const lowerCaseFileName = fileName.toLowerCase()
  if (lowerCaseFileName.endsWith('.xlsx')) {
    return PACKAGING_ITEM_DOCUMENT_XLSX_MIME
  }
  if (lowerCaseFileName.endsWith('.xlsm')) {
    return PACKAGING_ITEM_DOCUMENT_XLSM_MIME
  }
  if (lowerCaseFileName.endsWith('.png')) {
    return 'image/png'
  }
  if (lowerCaseFileName.endsWith('.bmp')) {
    return 'image/bmp'
  }
  if (
    lowerCaseFileName.endsWith('.tif') ||
    lowerCaseFileName.endsWith('.tiff')
  ) {
    return 'image/tiff'
  }
  if (
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.jpeg') ||
    lowerCaseFileName.endsWith('.jfif')
  ) {
    return 'image/jpeg'
  }
  return 'application/pdf'
}

/** Prefer browser MIME when allowlisted; otherwise resolve from extension. */
export function resolvePackagingItemDocumentContentType(file: File): string {
  if (
    !isIgnorableBrowserContentType(file.type) &&
    PACKAGING_ALLOWED_CONTENT_TYPES.has(file.type.trim().toLowerCase())
  ) {
    return file.type
  }
  return resolvePackagingContentTypeFromExtension(file.name)
}

export type PackagingItemDocumentUploadValidationReason =
  | 'empty'
  | 'unsupported-type'

export function getPackagingItemDocumentUploadValidationReason(
  file: File
): PackagingItemDocumentUploadValidationReason | null {
  if (file.size === 0) {
    return 'empty'
  }
  if (!isAllowedPackagingItemDocumentFile(file)) {
    return 'unsupported-type'
  }
  return null
}

export function allowsMultipleDocumentsForType(
  documentType: PackagingItemDocumentType
): boolean {
  return !SINGLE_UPLOAD_PACKAGING_DOCUMENT_TYPES.includes(documentType)
}

export function canUploadPackagingDocumentType(
  selectedDocumentType: PackagingItemDocumentType | null | undefined,
  existingDocumentTypes: readonly PackagingItemDocumentType[]
): boolean {
  if (selectedDocumentType == null) {
    return false
  }
  if (allowsMultipleDocumentsForType(selectedDocumentType)) {
    return true
  }
  return !existingDocumentTypes.includes(selectedDocumentType)
}

export const PACKAGING_ITEM_DOCUMENT_TYPES: PackagingItemDocumentType[] = [
  'DIELINE_DIAGRAM',
  'PACKAGING_PHOTOS',
  'LABEL_PHOTOS',
  'SDS_OR_BOM',
  'HEAVY_METALS_TEST_REPORT',
  'HEAVY_METALS_CONFORMITY_STATEMENT',
  'SUBSTANCES_OF_CONCERN_TEST_REPORT',
  'SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT',
  'OTHER',
  'OTHER_TEST_REPORT',
]

export function getVisibleDocumentTypes(item: {
  heavyMetalsStatus?: string | null
  substancesOfConcernStatus?: string | null
}): PackagingItemDocumentType[] {
  return PACKAGING_ITEM_DOCUMENT_TYPES.filter((documentType) => {
    if (documentType === 'HEAVY_METALS_TEST_REPORT') {
      return item.heavyMetalsStatus === 'SUFFICIENTLY_MINIMIZED'
    }
    if (documentType === 'HEAVY_METALS_CONFORMITY_STATEMENT') {
      return item.heavyMetalsStatus === 'NONE'
    }
    if (documentType === 'SUBSTANCES_OF_CONCERN_TEST_REPORT') {
      return item.substancesOfConcernStatus === 'SUFFICIENTLY_MINIMIZED'
    }
    if (documentType === 'SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT') {
      return item.substancesOfConcernStatus === 'NONE'
    }
    return true
  })
}

export type PackagingItemDocumentRequirements = {
  requiredTypes: PackagingItemDocumentType[]
  presentTypes: PackagingItemDocumentType[]
  missingRequiredTypes: PackagingItemDocumentType[]
}

export type PackagingItemDocumentTypeLabels = Record<
  PackagingItemDocumentType,
  string
>

export type PackagingItemDocumentsPanelLabels = {
  title: string
  uploadDocument: string
  uploading: string
  uploadFailed: string
  uploadRightsProtected: string
  uploadEncrypted: string
  uploadInvalidPdf: string
  uploadFileTooLarge: string
  loadingDocuments: string
  noDocuments: string
  download: string
  delete: string
  documentType: string
  selectDocumentType: string
  uploadDialogTitle: string
  uploadDialogDescription: string
  uploadDialogRequirementFormats: string
  uploadDialogRequirementMaxSize: string
  uploadDialogAssessingFile: string
  uploadDialogDragDrop: string
  uploadDialogOrClick: string
  uploadDialogSelectFiles: string
  uploadUnsupportedFileType: string
  uploadEmptyFile: string
  uploadDialogDone: string
  uploadDialogMultipleDropped: string
  uploadDialogConfirmUpload: string
  uploadDialogChooseDifferentFile: string
  librarySectionTitle: string
  librarySearchPlaceholder: string
  libraryEmpty: string
  libraryEmptyAllLinked: string
  librarySearchEmpty: string
  libraryLinkedItemCount: string
  libraryAttachAction: string
  libraryAttaching: string
  sameNameWarningTitle: string
  sameNameWarningDescription: string
  sameNameAlreadyLinkedDescription: string
  sameNameReuseCta: string
  attachSuccess: string
  uploadSuccess: string
  attachFailed: string
  uploadDialogTypeAlreadyUploaded: string
  columnType: string
  columnFile: string
  columnSize: string
  mandatoryDocuments: string
  missingMandatoryDocuments: string
  mandatoryComplete: string
  mandatoryMissing: string
  downloadHeavyMetalsStatement?: string
  downloadSubstancesOfConcernStatement?: string
  /** Long labels for upload/mandatory context. */
  typeLabels: PackagingItemDocumentTypeLabels
  /** Short labels for the documents table. */
  shortTypeLabels: PackagingItemDocumentTypeLabels
  ingestDocuments?: string
  ingestingDocuments?: string
  ingestDocument?: string
  retryDocument?: string
  reIngestDocument?: string
  columnIngestion?: string
  ingestionNotStarted?: string
  ingestionStatusLabels?: Partial<
    Record<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED', string>
  >
  linkedDuplicateBadge?: string
  linkedDuplicateTooltip?: string
  linkedDuplicateViewCanonical?: string
}

const DEFAULT_SHORT_TYPE_LABELS: PackagingItemDocumentTypeLabels = {
  DIELINE_DIAGRAM: 'Dieline',
  PACKAGING_PHOTOS: 'Packaging photos',
  LABEL_PHOTOS: 'Label photos',
  SDS_OR_BOM: 'SDS / BOM',
  HEAVY_METALS_TEST_REPORT: 'Heavy metals report',
  HEAVY_METALS_CONFORMITY_STATEMENT: 'Heavy metals statement',
  SUBSTANCES_OF_CONCERN_TEST_REPORT: 'SoC report',
  SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT: 'SoC statement',
  OTHER: 'Other',
  OTHER_TEST_REPORT: 'Other test report',
}

export const DEFAULT_PACKAGING_ITEM_DOCUMENTS_PANEL_LABELS: PackagingItemDocumentsPanelLabels =
  {
    title: 'Documents',
    uploadDocument: 'Upload document',
    uploading: 'Uploading…',
    uploadFailed: 'Upload failed. Please try again.',
    uploadRightsProtected:
      'This file is protected with Microsoft sensitivity labels or rights management. Save a copy without protection, or ask your supplier for an unprotected version, then upload again.',
    uploadEncrypted:
      'This file is password-encrypted. Remove the password or export an unencrypted copy, then upload again.',
    uploadInvalidPdf:
      'This file uses a .pdf extension but is not a readable PDF (for example a DRM wrapper). Export or save a normal PDF copy.',
    uploadFileTooLarge:
      'This file exceeds the 30 MB upload limit. Choose a smaller file and try again.',
    loadingDocuments: 'Loading documents…',
    noDocuments: 'No documents uploaded yet.',
    download: 'Download',
    delete: 'Delete',
    documentType: 'Document type',
    selectDocumentType: 'Select document type',
    uploadDialogTitle: 'Upload document',
    uploadDialogDescription:
      'Choose a document type, then drag and drop a file or browse to upload.',
    uploadDialogRequirementFormats:
      'Accepted formats: PDF, JPEG, PNG, BMP, TIFF, XLSX, XLSM',
    uploadDialogRequirementMaxSize: 'Maximum {{maxSizeMb}} MB per file',
    uploadDialogAssessingFile: 'Checking file…',
    uploadDialogDragDrop: 'Drag and drop a file here',
    uploadDialogOrClick: 'or click to browse',
    uploadDialogSelectFiles: 'Select file',
    uploadUnsupportedFileType:
      'This file type is not supported. Choose a PDF, image (JPEG, JFIF, PNG, BMP, TIFF), or spreadsheet (XLSX, XLSM) and try again.',
    uploadEmptyFile:
      'This file is empty (0 bytes). Choose a non-empty file and try again.',
    uploadDialogDone: 'Done',
    uploadDialogMultipleDropped:
      'You selected {{count}} files. Only "{{fileName}}" will be uploaded.',
    uploadDialogConfirmUpload: 'Upload',
    uploadDialogChooseDifferentFile: 'Choose a different file',
    librarySectionTitle: 'Reuse from supplier library',
    librarySearchPlaceholder: 'Search library files…',
    libraryEmpty: 'No completed files in the supplier library yet.',
    libraryEmptyAllLinked:
      'Every library file is already linked to this item for the selected document type. Upload a new file below, or select a file with the same name to see reuse options.',
    librarySearchEmpty: 'No library files match your search.',
    libraryLinkedItemCount: 'Linked to {{count}} items',
    libraryAttachAction: 'Use this file',
    libraryAttaching: 'Attaching…',
    sameNameWarningTitle: 'This file name already exists in the library',
    sameNameWarningDescription:
      'You can reuse the existing file instead of uploading a new copy.',
    sameNameAlreadyLinkedDescription:
      'This file is already linked to this item. Uploading will create another copy with the same name.',
    sameNameReuseCta: 'Reuse existing "{{fileName}}"',
    attachSuccess: 'File attached successfully.',
    uploadSuccess: 'File uploaded successfully.',
    attachFailed: 'Could not attach this file. Please try again.',
    uploadDialogTypeAlreadyUploaded:
      'A signed statement of this type is already uploaded. Delete it first to replace it.',
    columnType: 'Type',
    columnFile: 'File',
    columnSize: 'Size',
    mandatoryDocuments: 'Mandatory documents',
    missingMandatoryDocuments: 'Missing mandatory documents',
    mandatoryComplete: 'Uploaded',
    mandatoryMissing: 'Missing',
    typeLabels: {
      DIELINE_DIAGRAM: 'Dieline/diagram of packaging',
      PACKAGING_PHOTOS: 'Photos of packaging',
      LABEL_PHOTOS: 'Photos of packaging labels',
      SDS_OR_BOM: 'Safety Data Sheet (SDS) or Packaging Bill of Materials',
      HEAVY_METALS_TEST_REPORT:
        'Test report demonstrating the packaging contains a sum of concentration levels of lead, cadmium, mercury, and hexavalent chromium that does not exceed 100 mg/kg.',
      HEAVY_METALS_CONFORMITY_STATEMENT:
        'Signed statement certifying that lead, cadmium, mercury, and hexavalent chromium are not present in the packaging, as declared under PPWR.',
      SUBSTANCES_OF_CONCERN_TEST_REPORT:
        'Test report demonstrating the packaging contains substance/s of concern but that they have been minimised so as not to negatively impact safety, emissions or waste outcomes associated with the packaging.',
      SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT:
        'Signed statement certifying that substances of concern are not present in this packaging, as declared under PPWR.',
      OTHER:
        'Other supporting evidence that does not fit a dedicated packaging document type (for example supplier certificates).',
      OTHER_TEST_REPORT:
        'Additional laboratory or compliance test report for this packaging (for example misc. chemical or material tests).',
    },
    shortTypeLabels: DEFAULT_SHORT_TYPE_LABELS,
  }
